import { BidderAddress } from '@/types/BidderAddresses';
import { COMPLETE_ACCOUNT_FAIL, COMPLETE_ACCOUNT_REQUEST, COMPLETE_ACCOUNT_SUCCESS, DISMISS_MODAL } from './actions';
import { createSelector } from '@reduxjs/toolkit';
import { get2DigitCountryCode } from '@/utils/address';
import { getAuthToken, getBidderEmail, getBidderId } from './user';
import { getCompleteAccountAnalytics } from './analytics';
import { getDeployment } from './config';
import { getSeller } from './seller';
import { getSellerUrl } from '@/utils/urls';
import { getSignupFromFlattenTheCurvePage, getSignupReferralHouse } from './modal';
import { GlobalState } from '../rootReducer';
import { postAddBidderAddress, postUpdateProfile, UpdateProfileWithAddressRequest } from '@/redux/api/bidder';
import { push } from 'connected-react-router';

// reducer
export const DEFAULT_STATE = {
    error: false,
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case DISMISS_MODAL:
            return {
                ...state,
                error: false,
                submitted: false,
                success: false,
            };
        case COMPLETE_ACCOUNT_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        case COMPLETE_ACCOUNT_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case COMPLETE_ACCOUNT_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.completeAccount;

export const getUiCompleteAccount = createSelector(stateSelector, (state) => state);

/* ACTION CREATORS */
export const submitCompleteAccount =
    (updatedProfile: UpdateProfileWithAddressRequest) => async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            // email is not set in the complete account form, it comes from the token at this point
            updatedProfile.email = getBidderEmail(state);

            dispatch({
                type: COMPLETE_ACCOUNT_REQUEST,
            });

            const bidderID = getBidderId(state);
            const address: BidderAddress = {
                address1: updatedProfile.address1,
                address2: updatedProfile.address2,
                address3: '',
                addressId: 0,
                avalaraVerified: false,
                bidderId: bidderID,
                city: updatedProfile.city,
                countryCode: get2DigitCountryCode(updatedProfile.country),
                enabled: true,
                firstName: updatedProfile.firstName,
                isDefault: true,
                lastName: updatedProfile.lastName,
                organizationName: updatedProfile.organizationName,
                phoneNumber: updatedProfile.phoneNumber ? updatedProfile.phoneNumber : updatedProfile.mobile,
                postalCode: updatedProfile.zipcode,
                shippingOptionType: '',
                state: updatedProfile.state,
                verifiedStatus: 0,
            };

            await postAddBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
            });

            const response = await postUpdateProfile({
                authToken,
                deployment,
                profile: updatedProfile,
            });

            dispatch({
                meta: { ...getCompleteAccountAnalytics(), actionTime: Date.now() },
                payload: response.payload,
                type: COMPLETE_ACCOUNT_SUCCESS,
            });

            const signupReferralHouse = getSignupReferralHouse(state);
            const signupFromFlattenTheCurvePage = getSignupFromFlattenTheCurvePage(state);
            const seller = getSeller(state, signupReferralHouse);

            if (signupFromFlattenTheCurvePage) {
                if (seller.name) {
                    dispatch(push(getSellerUrl(seller.sellerId, seller.name)));
                } else {
                    dispatch(push('/'));
                }
            }
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: COMPLETE_ACCOUNT_FAIL,
            });
        }
    };
